/*
xrx-input.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useEffect, useRef, useState } from 'react'

export interface XrxInputProps {
  id: string
  placeholder: string
  label: string
  ariaLabel
  inputType: 'clearable' | 'general'
  onClear?(): void
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void
  value: string
  autoFocus: boolean
  valid: boolean | undefined
  validationMessages: string[]
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void
  closeKeyboardOnEnter?: boolean
}
export default function XrxInput (props: XrxInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState<boolean>();
  const showClearButton = props.value.length > 0 && isFocused === true
  useEffect(() => {
    // Automatically focus the input when the component mounts
    if (props.autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [])
  const handleClear = () =>{
    if(props.onClear){
        props.onClear();
        if (props.autoFocus && inputRef.current) {
            inputRef.current.focus()
          }
    }
  }
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) =>{
    if(props.onFocus){
        props.onFocus(event);
    }
    setIsFocused(true)
  }
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) =>{
    if(props.onBlur){
        props.onBlur(event);
    }
    setIsFocused(false)
  }
  const closeEmbeddedKeyboard = () => {
    const maybeCloseKeyboard = (
      globalThis as typeof globalThis & {
        EIP_CloseEmbeddedKeyboard?: () => void
      }
    ).EIP_CloseEmbeddedKeyboard

    if (typeof maybeCloseKeyboard === 'function') {
      maybeCloseKeyboard()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.onKeyDown) {
      props.onKeyDown(event)
    }

    if (event.defaultPrevented) {
      return
    }

    if (props.closeKeyboardOnEnter !== false && event.key === 'Enter') {
      closeEmbeddedKeyboard()
    }
  }
  return (
    <>
      <label htmlFor={`${props.id}-input`} className='input-label'>
        {props.label}
      </label>
      <div
        id={`${props.id}-input`}
        className={`xrx-input ${
          props.valid === false ? 'input-validation-danger mb-0' : ''
        } ${isFocused === true ? 'input-hover input-focus input-active input-active-focus' : ''}`}
      >
        <div className='input-group'>
          <input
            id={props.id}
            ref={inputRef}
            type='text'
            className='form-control px-2'
            placeholder={props.placeholder}
            aria-label={props.ariaLabel}
            value={props.value}
            onChange={props.onChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
          />
          <div className='input-group-append'>
            {showClearButton && 
            <button
              className='xrx-btn btn-inline btn-borderless btn-glyph xrx-input-clear-btn'
              type='button'
              onMouseDown={event => event.preventDefault()}
              onPointerDown={event => event.preventDefault()}
              onClick={()=>handleClear()}
            >
              <i className='xgl-clear_all_two_tone xglsize-32 xrx-input-clear-icon' ></i>
            </button>
            }
            {props.value.length > 0 && props.valid === false &&
              <div className='input-group-divider'></div>
            }
          </div>
        </div>
      </div>
      {props.valid === false ? (
        <>
          <div
            className={`form-text mt-2${
              props.validationMessages.length > 1 ? 'multiline' : ''
            }`}
          >
            {props.validationMessages.map((m, i) => {
              return (
                <>
                <div className='xrx-stacked-icon stacked-icon-size-32'>
                <i className='xgl-danger_stack_bkgd xglsize-32'></i>
                <i className='xgl-danger_stack_mdgd xglsize-32'></i>
                <i className='xgl-danger_stack_frgd xglsize-32'></i>
              </div>
                  <div key={i} className='message'>{m}</div>
                  </>
              )
            })}
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  )
}
